import React, { useState, useEffect, useCallback } from 'react';
import {
    CheckCircle2, XCircle, MinusCircle, Clock, RefreshCw,
    ChevronDown, ChevronUp, Code2, Play, FileCode2, Terminal,
    TrendingUp, Zap, ShieldCheck, AlertTriangle, Ban, Wrench, X, Copy, RotateCcw,
    FlaskConical, Lock, Trash2
} from 'lucide-react';
import { api } from '../../services/api';

// ─── Types ────────────────────────────────────────────────────────────────────

type QAState = 'PENDING_REVIEW' | 'VERIFIED' | 'DEFECT' | 'TEST_ISSUE' | 'BLOCKED';
type TriageType = 'DEFECT' | 'TEST_ISSUE' | 'BLOCKED';
type IssueStatus = 'Abierto' | 'En progreso' | 'Resuelto' | 'Cerrado';
type PoolState = 'IN_REGRESSION' | 'QUARANTINED' | null;

interface TriageEntry {
    qa_state: QAState;
    triage_type?: TriageType | null;
    triage_note?: string | null;
    defect_link?: string | null;
    responsable?: string | null;
    accion_siguiente?: string | null;
    issue_id?: string | null;
    issue_status?: IssueStatus | null;
    pool_state?: PoolState;
    pool_added_at?: string | null;
    quarantine_reason?: string | null;
    quarantine_until?: string | null;
    triaged_by?: string;
    triaged_at?: string;
}

interface TestResult {
    name: string;
    status: 'pass' | 'fail' | 'skip';
    duration_ms?: number;
    error?: string;
}

interface ExecutionResult {
    id: string;
    run_id: string;
    entity_id: string;
    total_tests: number;
    passed: number;
    failed: number;
    skipped: number;
    pass_rate: number;
    framework: string | null;
    source: string;
    received_at: string | null;
    results: TestResult[] | null;
    summary: {
        pass: number;
        fail: number;
        skip: number;
        // Traceability
        machine_name?: string;
        machine_ip?: string;
        os_info?: string;
        executor?: string;
        started_at?: string;
        finished_at?: string;
        env_label?: string;
        runner_version?: string;
    } | null;
    triage: Record<string, TriageEntry>;
}

interface AutomationStatus {
    entity_id: string;
    has_automation_code: boolean;
    automation_code: {
        suite_id: string;
        version: number;
        mode: string;
        framework: string;
        total_files: number;
        filenames: string[];
        created_at: string | null;
    } | null;
    has_executions: boolean;
    latest_execution: {
        run_id: string;
        total_tests: number;
        passed: number;
        failed: number;
        skipped: number;
        pass_rate: number;
        framework: string | null;
        received_at: string | null;
    } | null;
    total_executions: number;
}

interface Props {
    entityId: string;
    projectId: string;
}

// ─── QA State Badge ──────────────────────────────────────────────────────────

const QA_STATE_CONFIG: Record<QAState, { label: string; color: string; Icon: React.ElementType }> = {
    PENDING_REVIEW: { label: 'Pendiente',  color: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20', Icon: Clock },
    VERIFIED:       { label: 'Verificado', color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20', Icon: ShieldCheck },
    DEFECT:         { label: 'Defecto',    color: 'bg-red-500/10 text-red-400 border-red-500/20', Icon: AlertTriangle },
    TEST_ISSUE:     { label: 'Test Issue', color: 'bg-orange-500/10 text-orange-400 border-orange-500/20', Icon: Wrench },
    BLOCKED:        { label: 'Bloqueado',  color: 'bg-slate-500/10 text-slate-400 border-slate-500/20', Icon: Ban },
};

const QAStateBadge: React.FC<{ state: QAState }> = ({ state }) => {
    const { label, color, Icon } = QA_STATE_CONFIG[state] ?? QA_STATE_CONFIG.PENDING_REVIEW;
    return (
        <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border ${color}`}>
            <Icon className="w-3 h-3" />
            {label}
        </span>
    );
};

// ─── Pool State Badge ──────────────────────────────────────────────────────────

const PoolStateBadge: React.FC<{ state: PoolState; until?: string | null }> = ({ state, until }) => {
    if (!state) return null;
    const isExpired = until ? new Date(until) < new Date() : false;
    if (state === 'IN_REGRESSION') {
        return (
            <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border bg-cyan-500/10 text-cyan-400 border-cyan-500/20">
                <FlaskConical className="w-3 h-3" /> Regresión
            </span>
        );
    }
    return (
        <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border ${
            isExpired ? 'bg-red-500/10 text-red-400 border-red-500/20' : 'bg-slate-500/10 text-slate-400 border-slate-500/20'
        }`}>
            <Lock className="w-3 h-3" />
            {isExpired ? '⚠ Cuarentena vencida' : 'Cuarentena'}
            {until && <span className="opacity-60 ml-0.5 text-[10px]">{until}</span>}
        </span>
    );
};

// ─── Framework Badge ──────────────────────────────────────────────────────────

const FRAMEWORK_CONFIG: Record<string, { color: string }> = {
    'playwright_ts': { color: 'bg-blue-500/10 text-blue-400 border-blue-500/20' },
    'cypress':       { color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' },
    'maestro':       { color: 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20' },
};

const FrameworkBadge: React.FC<{ framework: string | null }> = ({ framework }) => {
    if (!framework) return null;
    const config = FRAMEWORK_CONFIG[framework] || { color: 'bg-slate-700/60 text-slate-300 border-slate-700/50' };
    return (
        <span className={`text-xs px-2 py-0.5 rounded-full border ${config.color}`}>
            {framework}
        </span>
    );
};


// ─── Quarantine Modal ─────────────────────────────────────────────────────────

interface QuarantineModalProps {
    caseName: string;
    onClose: () => void;
    onSubmit: (reason: string, until_date?: string) => Promise<void>;
}

const QuarantineModal: React.FC<QuarantineModalProps> = ({ caseName, onClose, onSubmit }) => {
    const [reason, setReason]     = useState('');
    const [untilDate, setUntilDate] = useState('');
    const [saving, setSaving]     = useState(false);

    const handleSubmit = async () => {
        if (!reason.trim()) return;
        setSaving(true);
        try {
            await onSubmit(reason.trim(), untilDate || undefined);
            onClose();
        } finally {
            setSaving(false);
        }
    };

    // Default until_date = today + 7 days
    const defaultUntil = new Date(Date.now() + 7 * 864e5).toISOString().split('T')[0];

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="bg-slate-900 border border-slate-700 rounded-2xl p-6 w-full max-w-md shadow-2xl">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-white font-semibold text-sm flex items-center gap-2">
                        <Lock className="w-4 h-4 text-slate-400" /> Poner en Cuarentena
                    </h3>
                    <button onClick={onClose} className="text-slate-500 hover:text-white transition-colors">
                        <X className="w-4 h-4" />
                    </button>
                </div>

                <p className="text-xs text-slate-400 mb-4 font-mono bg-slate-800/60 px-3 py-2 rounded-lg truncate">{caseName}</p>

                <p className="text-xs text-slate-500 mb-4 leading-relaxed">
                    El test quedará <span className="text-slate-300">excluido del conteo de pendientes</span> hasta la fecha indicada. Seguirá siendo trackeado pero no bloqueará el sprint.
                </p>

                <div className="mb-3">
                    <label className="text-xs text-slate-400 block mb-1">Razón de cuarentena <span className="text-red-400">*</span></label>
                    <textarea
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        rows={2}
                        placeholder="Ej: Flaky por ambiente — data inconsistente en staging"
                        className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-600 focus:outline-none focus:border-slate-500 resize-none"
                    />
                </div>

                <div className="mb-5">
                    <label className="text-xs text-slate-400 block mb-1">Cuarentena hasta (opcional, default: 7 días)</label>
                    <input
                        type="date"
                        value={untilDate || defaultUntil}
                        onChange={(e) => setUntilDate(e.target.value)}
                        className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-slate-500"
                    />
                </div>

                <div className="flex gap-3">
                    <button onClick={onClose} className="flex-1 px-4 py-2 text-sm text-slate-400 border border-slate-700 rounded-xl hover:border-slate-600 transition-colors">
                        Cancelar
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={saving || !reason.trim()}
                        className="flex-1 px-4 py-2 text-sm bg-slate-600 text-white rounded-xl hover:bg-slate-500 transition-colors disabled:opacity-50"
                    >
                        {saving ? 'Guardando...' : 'Poner en Cuarentena'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─── Triage Modal ─────────────────────────────────────────────────────────────

interface TriageModalProps {
    caseName: string;
    testStatus: 'pass' | 'fail' | 'skip';
    current?: TriageEntry;
    onClose: () => void;
    onSubmit: (
        qa_state: QAState,
        triage_type?: TriageType,
        note?: string,
        link?: string,
        responsable?: string,
        accion_siguiente?: string
    ) => Promise<void>;
}

const TriageModal: React.FC<TriageModalProps> = ({ caseName, testStatus, current, onClose, onSubmit }) => {
    const [triageType, setTriageType]         = useState<TriageType>(current?.triage_type ?? 'DEFECT');
    const [note, setNote]                     = useState(current?.triage_note ?? '');
    const [link, setLink]                     = useState(current?.defect_link ?? '');
    const [responsable, setResponsable]       = useState(current?.responsable ?? 'QA');
    const [accionSig, setAccionSig]           = useState(current?.accion_siguiente ?? 'Corregir app');
    const [saving, setSaving]                 = useState(false);

    const handleSubmit = async () => {
        setSaving(true);
        try {
            const qa_state = testStatus === 'pass' ? 'VERIFIED' : triageType;
            await onSubmit(
                qa_state,
                testStatus !== 'pass' ? triageType : undefined,
                note,
                link,
                testStatus !== 'pass' ? responsable : undefined,
                testStatus !== 'pass' ? accionSig : undefined,
            );
            onClose();
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="bg-slate-900 border border-slate-700 rounded-2xl p-6 w-full max-w-md shadow-2xl">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-white font-semibold text-sm">
                        {testStatus === 'pass' ? 'Aprobar test' : 'Triaje de fallo'}
                    </h3>
                    <button onClick={onClose} className="text-slate-500 hover:text-white transition-colors">
                        <X className="w-4 h-4" />
                    </button>
                </div>

                <p className="text-xs text-slate-400 mb-4 font-mono bg-slate-800/60 px-3 py-2 rounded-lg truncate">
                    {caseName}
                </p>

                {testStatus !== 'pass' && (
                    <div className="mb-4">
                        <label className="text-xs text-slate-400 block mb-2">Tipo de falla</label>
                        <div className="grid grid-cols-3 gap-2">
                            {(['DEFECT', 'TEST_ISSUE', 'BLOCKED'] as TriageType[]).map((t) => {
                                const { label, Icon } = QA_STATE_CONFIG[t];
                                return (
                                    <button
                                        key={t}
                                        onClick={() => setTriageType(t)}
                                        className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border text-xs transition-all ${
                                            triageType === t
                                                ? QA_STATE_CONFIG[t].color
                                                : 'border-slate-700/50 text-slate-500 hover:border-slate-600'
                                        }`}
                                    >
                                        <Icon className="w-4 h-4" />
                                        {label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}

                <div className="mb-3">
                    <label className="text-xs text-slate-400 block mb-1">
                        {testStatus === 'pass' ? 'Nota (opcional)' : 'Descripción del problema'}
                    </label>
                    <textarea
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        rows={3}
                        placeholder={testStatus === 'pass' ? 'Comentario opcional...' : 'Ej: La app no expone /api/v1/balance separado'}
                        className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-600 focus:outline-none focus:border-slate-500 resize-none"
                    />
                </div>

                {testStatus !== 'pass' && (
                    <div className="mb-4 grid grid-cols-2 gap-3">
                        {/* Responsable */}
                        <div>
                            <label className="text-xs text-slate-400 block mb-1">Responsable</label>
                            <select
                                value={responsable}
                                onChange={(e) => setResponsable(e.target.value)}
                                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-slate-500"
                            >
                                {['QA', 'Dev', 'Ops', 'PO'].map(r => (
                                    <option key={r} value={r}>{r}</option>
                                ))}
                            </select>
                        </div>
                        {/* Acción siguiente */}
                        <div>
                            <label className="text-xs text-slate-400 block mb-1">Acción siguiente</label>
                            <select
                                value={accionSig}
                                onChange={(e) => setAccionSig(e.target.value)}
                                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-slate-500"
                            >
                                {['Corregir app', 'Corregir test', 'Esperar ambiente/datos', 'Re-ejecutar'].map(a => (
                                    <option key={a} value={a}>{a}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                )}

                {testStatus !== 'pass' && (
                    <div className="mb-3">
                        <label className="text-xs text-slate-400 block mb-1">Ref. externa (Jira/GitHub, opcional)</label>
                        <input
                            value={link}
                            onChange={(e) => setLink(e.target.value)}
                            placeholder="JIRA-123, GH-456 o URL externa"
                            className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-600 focus:outline-none focus:border-slate-500"
                        />
                    </div>
                )}

                <div className="flex gap-3">
                    <button
                        onClick={onClose}
                        className="flex-1 px-4 py-2 text-sm text-slate-400 border border-slate-700 rounded-xl hover:border-slate-600 transition-colors"
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={saving}
                        className={`flex-1 px-4 py-2 text-sm font-medium rounded-xl transition-all ${
                            testStatus === 'pass'
                                ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/30'
                                : 'bg-purple-500/20 text-purple-400 border border-purple-500/30 hover:bg-purple-500/30'
                        } disabled:opacity-50`}
                    >
                        {saving ? 'Guardando...' : testStatus === 'pass' ? 'Aprobar' : 'Guardar triaje'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const McpResultsPanel: React.FC<Props> = ({ entityId, projectId }) => {
    const [results, setResults]         = useState<ExecutionResult[]>([]);
    const [status, setStatus]           = useState<AutomationStatus | null>(null);
    const [loading, setLoading]         = useState(true);
    const [error, setError]             = useState<string | null>(null);
    const [expandedRun, setExpandedRun] = useState<string | null>(null);
    const [triageModal, setTriageModal] = useState<{ execId: string; test: TestResult } | null>(null);
    const [approvingAll, setApprovingAll] = useState<string | null>(null);
    const [reRunPanel, setReRunPanel]   = useState<string | null>(null);
    const [copied, setCopied]           = useState(false);
    const [quarantineModal, setQuarantineModal] = useState<{ execId: string; test: TestResult } | null>(null);

    const loadData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [statusData, resultsData] = await Promise.all([
                api.getAutomationStatus(projectId, entityId),
                api.getMcpResultsByEntity(entityId),
            ]);
            setStatus(statusData);
            setResults(resultsData);
        } catch (err: any) {
            setError(err?.response?.data?.detail || 'Error cargando datos');
        } finally {
            setLoading(false);
        }
    }, [entityId, projectId]);

    useEffect(() => { loadData(); }, [loadData]);

    // ── API calls ──────────────────────────────────────────────────────────────

    const handleTriage = async (
        execId: string,
        caseName: string,
        qa_state: QAState,
        triage_type?: TriageType,
        triage_note?: string,
        defect_link?: string,
        responsable?: string,
        accion_siguiente?: string
    ) => {
        // Auto-generate issue_id for first-time FAIL triage
        const existingIssueId = results.find(r => r.id === execId)?.triage?.[caseName]?.issue_id;
        const issue_id = qa_state !== 'VERIFIED' && qa_state !== 'PENDING_REVIEW'
            ? (existingIssueId ?? `FSQ-${String(Math.floor(Math.random() * 900000) + 100000)}`)
            : null;
        const issue_status: IssueStatus | null = issue_id
            ? (results.find(r => r.id === execId)?.triage?.[caseName]?.issue_status ?? 'Abierto')
            : null;

        await api.triageTestResult(execId, caseName, {
            qa_state, triage_type, triage_note, defect_link,
            responsable, accion_siguiente,
            issue_id, issue_status
        });
        // Update local state optimistically
        setResults(prev => prev.map(r => {
            if (r.id !== execId) return r;
            return {
                ...r,
                triage: {
                    ...r.triage,
                    [caseName]: {
                        qa_state,
                        triage_type: triage_type ?? null,
                        triage_note: triage_note ?? null,
                        defect_link: defect_link ?? null,
                        responsable: responsable ?? null,
                        accion_siguiente: accion_siguiente ?? null,
                        issue_id: issue_id ?? null,
                        issue_status: issue_status ?? null,
                    },
                }
            };
        }));
    };

    const handleApproveAll = async (execId: string) => {
        setApprovingAll(execId);
        try {
            await api.approveAllPassed(execId);
            setResults(prev => prev.map(r => {
                if (r.id !== execId) return r;
                const newTriage = { ...r.triage };
                (r.results || []).forEach(t => {
                    if (t.status === 'pass') {
                        newTriage[t.name] = { qa_state: 'VERIFIED', triage_note: 'Bulk approved' };
                    }
                });
                return { ...r, triage: newTriage };
            }));
        } finally {
            setApprovingAll(null);
        }
    };

    const handleDeleteRun = async (e: React.MouseEvent, execId: string) => {
        e.stopPropagation();
        if (!window.confirm('¿Eliminar permanentemente esta ejecución de prueba?')) return;
        try {
            await api.deleteExecutionResult(execId);
            setResults(prev => prev.filter(r => r.id !== execId));
            if (expandedRun === execId) setExpandedRun(null);
            loadData(); // Actualiza los contadores del panel superior
        } catch (err: any) {
            alert('Error al eliminar: ' + (err?.response?.data?.detail || err.message));
        }
    };

    // ── Helpers ────────────────────────────────────────────────────────────────

    const getQAState = (exec: ExecutionResult, testName: string): QAState =>
        exec.triage?.[testName]?.qa_state ?? 'PENDING_REVIEW';

    const getPendingCount = (exec: ExecutionResult) =>
        (exec.results ?? []).filter(t => getQAState(exec, t.name) === 'PENDING_REVIEW').length;

    const getPendingTests = (exec: ExecutionResult) =>
        (exec.results ?? []).filter(t => getQAState(exec, t.name) === 'PENDING_REVIEW');

    const updateIssueStatus = async (execId: string, caseName: string, issue_status: IssueStatus) => {
        const entry = results.find(r => r.id === execId)?.triage?.[caseName];
        if (!entry) return;
        await api.triageTestResult(execId, caseName, { ...entry, issue_status } as any);
        setResults(prev => prev.map(r => {
            if (r.id !== execId) return r;
            return {
                ...r,
                triage: {
                    ...r.triage,
                    [caseName]: { ...r.triage?.[caseName], issue_status },
                }
            };
        }));
    };

    const handleAddToPool = async (execId: string, caseName: string) => {
        await api.addToPool(execId, caseName);
        setResults(prev => prev.map(r => {
            if (r.id !== execId) return r;
            return {
                ...r,
                triage: {
                    ...r.triage,
                    [caseName]: {
                        ...r.triage?.[caseName],
                        pool_state: 'IN_REGRESSION' as PoolState,
                        pool_added_at: new Date().toISOString(),
                    },
                }
            };
        }));
    };

    const handleQuarantine = async (execId: string, caseName: string, reason: string, until_date?: string) => {
        await api.quarantineTest(execId, caseName, { reason, until_date });
        setResults(prev => prev.map(r => {
            if (r.id !== execId) return r;
            return {
                ...r,
                triage: {
                    ...r.triage,
                    [caseName]: {
                        ...r.triage?.[caseName],
                        pool_state: 'QUARANTINED' as PoolState,
                        quarantine_reason: reason,
                        quarantine_until: until_date ?? null,
                    },
                }
            };
        }));
    };

    // ── Render ─────────────────────────────────────────────────────────────────

    if (loading) {
        return (
            <div className="flex items-center justify-center py-16">
                <RefreshCw className="w-6 h-6 text-slate-400 animate-spin" />
                <span className="ml-3 text-slate-400">Cargando dashboard...</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center py-16">
                <XCircle className="w-8 h-8 text-red-400 mb-3" />
                <p className="text-red-400 text-sm">{error}</p>
                <button onClick={loadData} className="mt-4 text-sm text-purple-400 hover:text-purple-300">Reintentar</button>
            </div>
        );
    }

    const hasCode   = status?.has_automation_code;
    const latestExec = status?.latest_execution;
    const code      = status?.automation_code;

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="text-lg font-semibold text-white">Dashboard de Automatizacion</h3>
                    <p className="text-xs text-slate-500 mt-0.5">Estado de tests automatizados para {entityId}</p>
                </div>
                <button
                    onClick={loadData}
                    className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-white transition-colors px-3 py-1.5 rounded-lg border border-slate-700/50 hover:border-slate-600"
                >
                    <RefreshCw className="w-3.5 h-3.5" />
                    Actualizar
                </button>
            </div>

            {/* Status Cards Row */}
            <div className="grid grid-cols-3 gap-4">
                {/* Card 1: Code Generation */}
                <div className={`rounded-xl border p-4 ${hasCode ? 'border-emerald-500/20 bg-emerald-500/5' : 'border-slate-700/50 bg-slate-800/30'}`}>
                    <div className="flex items-center gap-2 mb-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${hasCode ? 'bg-emerald-500/20' : 'bg-slate-700/40'}`}>
                            <Code2 className={`w-4 h-4 ${hasCode ? 'text-emerald-400' : 'text-slate-500'}`} />
                        </div>
                        <span className="text-xs font-medium text-slate-400 uppercase tracking-wide">Codigo</span>
                    </div>
                    {hasCode && code ? (
                        <>
                            <p className="text-2xl font-bold text-white">{code.total_files} archivos</p>
                            <p className="text-xs text-slate-400 mt-1">{code.framework} &middot; v{code.version} ({code.mode})</p>
                        </>
                    ) : (
                        <>
                            <p className="text-lg font-semibold text-slate-500">Sin generar</p>
                            <p className="text-xs text-slate-600 mt-1">Genera codigo desde Test Cases</p>
                        </>
                    )}
                </div>

                {/* Card 2: Latest Execution */}
                <div className={`rounded-xl border p-4 ${latestExec ? (latestExec.failed === 0 ? 'border-emerald-500/20 bg-emerald-500/5' : 'border-yellow-500/20 bg-yellow-500/5') : 'border-slate-700/50 bg-slate-800/30'}`}>
                    <div className="flex items-center gap-2 mb-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${latestExec ? (latestExec.failed === 0 ? 'bg-emerald-500/20' : 'bg-yellow-500/20') : 'bg-slate-700/40'}`}>
                            <Play className={`w-4 h-4 ${latestExec ? (latestExec.failed === 0 ? 'text-emerald-400' : 'text-yellow-400') : 'text-slate-500'}`} />
                        </div>
                        <span className="text-xs font-medium text-slate-400 uppercase tracking-wide">Ultima ejecucion</span>
                    </div>
                    {latestExec ? (
                        <>
                            <p className="text-2xl font-bold text-white">{latestExec.pass_rate}%</p>
                            <div className="flex items-center gap-2 mt-1">
                                <span className="text-xs text-emerald-400">{latestExec.passed} pass</span>
                                <span className="text-xs text-slate-600">&middot;</span>
                                <span className="text-xs text-red-400">{latestExec.failed} fail</span>
                            </div>
                        </>
                    ) : (
                        <>
                            <p className="text-lg font-semibold text-slate-500">Sin ejecutar</p>
                            <p className="text-xs text-slate-600 mt-1">Ejecuta desde tu IDE con MCP</p>
                        </>
                    )}
                </div>

                {/* Card 3: Total Executions */}
                <div className={`rounded-xl border p-4 ${(status?.total_executions ?? 0) > 0 ? 'border-purple-500/20 bg-purple-500/5' : 'border-slate-700/50 bg-slate-800/30'}`}>
                    <div className="flex items-center gap-2 mb-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${(status?.total_executions ?? 0) > 0 ? 'bg-purple-500/20' : 'bg-slate-700/40'}`}>
                            <TrendingUp className={`w-4 h-4 ${(status?.total_executions ?? 0) > 0 ? 'text-purple-400' : 'text-slate-500'}`} />
                        </div>
                        <span className="text-xs font-medium text-slate-400 uppercase tracking-wide">Ejecuciones</span>
                    </div>
                    <p className="text-2xl font-bold text-white">{status?.total_executions ?? 0}</p>
                    <p className="text-xs text-slate-400 mt-1">
                        {(status?.total_executions ?? 0) > 0 ? 'ejecuciones registradas' : 'sin historial aun'}
                    </p>
                </div>
            </div>

            {/* Automation Files Preview */}
            {hasCode && code && (
                <div className="rounded-xl border border-slate-700/50 bg-slate-800/20 p-4">
                    <div className="flex items-center gap-2 mb-3">
                        <FileCode2 className="w-4 h-4 text-purple-400" />
                        <span className="text-sm font-medium text-white">Archivos Generados</span>
                        <FrameworkBadge framework={code.framework} />
                    </div>

                    <div className="grid grid-cols-3 gap-2">
                        {code.filenames.map((name, i) => (
                            <div key={i} className="flex items-center gap-2 px-3 py-2 bg-slate-800/60 rounded-lg border border-slate-700/30">
                                <Terminal className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
                                <span className="text-xs text-slate-300 truncate">{name}</span>
                            </div>
                        ))}
                    </div>
                    <p className="text-xs text-slate-500 mt-3">
                        Usa <code className="text-purple-400 bg-slate-800/80 px-1.5 py-0.5 rounded">setup_and_run</code> en tu IDE para desplegar y ejecutar estos archivos
                    </p>
                </div>
            )}

            {/* Setup Guide */}
            {!hasCode && !latestExec && (
                <div className="rounded-xl border border-slate-700/50 bg-slate-800/20 p-6">
                    <div className="flex items-center gap-2 mb-4">
                        <Zap className="w-4 h-4 text-yellow-400" />
                        <span className="text-sm font-medium text-white">Como empezar</span>
                    </div>
                    <div className="space-y-3">
                        {[
                            'Genera casos de prueba en la pestana Test Cases',
                            'Genera codigo automatizado con el boton Generate Automation',
                            'Conecta tu IDE con @smartcase/mcp y ejecuta los tests',
                        ].map((label, i) => (
                            <div key={i} className="flex items-center gap-3 text-sm">
                                <span className="w-6 h-6 rounded-full bg-purple-500/20 border border-purple-500/40 flex items-center justify-center text-purple-300 text-xs font-bold flex-shrink-0">
                                    {i + 1}
                                </span>
                                <span className="text-slate-400">{label}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Execution History */}
            {results.length > 0 && (
                <div>
                    <h4 className="text-sm font-semibold text-white mb-3">Historial de Ejecuciones</h4>
                    <div className="space-y-3">
                        {results.map((result) => {
                            const isExpanded = expandedRun === result.id;
                            const pendingCount = getPendingCount(result);
                            const statusBg = result.failed === 0
                                ? 'border-emerald-500/20 bg-emerald-500/5'
                                : result.failed > result.passed
                                    ? 'border-red-500/20 bg-red-500/5'
                                    : 'border-yellow-500/20 bg-yellow-500/5';

                            return (
                                <div key={result.id} className={`rounded-xl border ${statusBg} overflow-hidden`}>
                                    {/* Run Header */}
                                    <div
                                        onClick={() => setExpandedRun(isExpanded ? null : result.id)}
                                        className="w-full flex items-center gap-4 p-4 hover:bg-white/5 transition-colors text-left cursor-pointer"
                                    >
                                        <div className="flex-shrink-0">
                                            {result.failed === 0
                                                ? <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                                                : result.failed > result.passed
                                                    ? <XCircle className="w-5 h-5 text-red-400" />
                                                    : <MinusCircle className="w-5 h-5 text-yellow-400" />}
                                        </div>

                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className="text-white font-medium text-sm truncate">
                                                    Run {result.run_id.slice(0, 8)}...
                                                </span>
                                                <FrameworkBadge framework={result.framework} />
                                                {pendingCount > 0 && (
                                                    <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-500/10 border border-yellow-500/20 text-yellow-400">
                                                        {pendingCount} pendiente{pendingCount !== 1 ? 's' : ''}
                                                    </span>
                                                )}
                                            </div>

                                            {/* Fecha y hora de ejecución */}
                                            <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                                                {result.summary?.started_at && (
                                                    <div className="flex items-center gap-1">
                                                        <Clock className="w-3 h-3 text-slate-500" />
                                                        <span className="text-xs text-slate-500">
                                                            {new Date(result.summary.started_at).toLocaleString()}
                                                        </span>
                                                        {result.summary.finished_at && (
                                                            <span className="text-xs text-slate-600">
                                                                · {Math.round((new Date(result.summary.finished_at).getTime() - new Date(result.summary.started_at).getTime()) / 1000)}s
                                                            </span>
                                                        )}
                                                    </div>
                                                )}
                                                {!result.summary?.started_at && result.received_at && (
                                                    <div className="flex items-center gap-1">
                                                        <Clock className="w-3 h-3 text-slate-500" />
                                                        <span className="text-xs text-slate-500">
                                                            {new Date(result.received_at).toLocaleString()}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Trazabilidad de máquina — siempre visible */}
                                            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                                <span className="text-xs text-slate-600">
                                                    🖥 {result.summary?.machine_name ?? '—'}
                                                </span>
                                                <span className="text-xs text-slate-600">
                                                    · {result.summary?.machine_ip ?? '—'}
                                                </span>
                                                <span className="text-xs text-slate-600">
                                                    · {result.summary?.os_info ?? '—'}
                                                </span>
                                                <span className="text-xs px-1.5 py-0.5 rounded bg-slate-700/50 text-slate-400">
                                                    {result.summary?.executor ?? '—'}
                                                </span>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-3">
                                            <span className="text-sm text-emerald-400">{result.passed}</span>
                                            <span className="text-sm text-red-400">{result.failed}</span>
                                            {result.skipped > 0 && <span className="text-sm text-slate-400">{result.skipped}</span>}
                                            <span className="text-sm font-bold text-white">{result.pass_rate}%</span>
                                        </div>

                                        <div className="w-20 h-2 rounded-full bg-slate-700 overflow-hidden">
                                            <div
                                                className={`h-full rounded-full ${
                                                    result.pass_rate === 100 ? 'bg-emerald-400' :
                                                    result.pass_rate >= 70 ? 'bg-yellow-400' : 'bg-red-400'
                                                }`}
                                                style={{ width: `${result.pass_rate}%` }}
                                            />
                                        </div>

                                        <div className="flex items-center gap-3 ml-2">
                                            <button 
                                                onClick={(e) => handleDeleteRun(e, result.id)}
                                                className="p-1.5 rounded-lg text-slate-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                                                title="Eliminar ejecución"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                            {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                                        </div>
                                    </div>

                                    {/* Expanded: Test Table with QA Triage */}
                                    {isExpanded && result.results && (
                                        <div className="border-t border-slate-700/50 p-4">
                                            {/* ── KPI Summary Bar ─────────────────────────────────────── */}
                                            {(() => {
                                                const total   = result.results.length;
                                                const passed  = result.results.filter(t => t.status === 'pass').length;
                                                const verified = result.results.filter(t =>
                                                    result.triage?.[t.name]?.qa_state === 'VERIFIED'
                                                ).length;
                                                const pending = result.results.filter(t =>
                                                    (result.triage?.[t.name]?.qa_state ?? 'PENDING_REVIEW') === 'PENDING_REVIEW'
                                                ).length;
                                                const pPassRate  = total ? Math.round((passed   / total) * 100) : 0;
                                                const pVerified  = total ? Math.round((verified / total) * 100) : 0;
                                                const pPending   = total ? Math.round((pending  / total) * 100) : 0;

                                                const kpis = [
                                                    {
                                                        label: 'Pass Rate',
                                                        sub: '¿Cómo salió la ejecución?',
                                                        value: `${passed}/${total}`,
                                                        pct: pPassRate,
                                                        barColor: pPassRate === 100 ? 'bg-emerald-400' : pPassRate >= 70 ? 'bg-yellow-400' : 'bg-red-400',
                                                        textColor: pPassRate === 100 ? 'text-emerald-400' : pPassRate >= 70 ? 'text-yellow-400' : 'text-red-400',
                                                        badge: `${pPassRate}%`,
                                                    },
                                                    {
                                                        label: 'Verificado QA',
                                                        sub: '¿Cuánto está aprobado por QA?',
                                                        value: `${verified}/${total}`,
                                                        pct: pVerified,
                                                        barColor: pVerified === 100 ? 'bg-emerald-400' : pVerified >= 70 ? 'bg-cyan-400' : 'bg-slate-500',
                                                        textColor: pVerified === 100 ? 'text-emerald-400' : pVerified >= 70 ? 'text-cyan-400' : 'text-slate-400',
                                                        badge: `${pVerified}%`,
                                                    },
                                                    {
                                                        label: 'Pendiente QA',
                                                        sub: '¿Qué queda por revisar?',
                                                        value: `${pending}/${total}`,
                                                        pct: pPending,
                                                        barColor: pending === 0 ? 'bg-emerald-400' : pPending <= 30 ? 'bg-yellow-400' : 'bg-red-400',
                                                        textColor: pending === 0 ? 'text-emerald-400' : pPending <= 30 ? 'text-yellow-400' : 'text-red-400',
                                                        badge: pending === 0 ? '✓ Listo' : `${pPending}%`,
                                                    },
                                                ];

                                                return (
                                                    <div className="grid grid-cols-3 gap-3 mb-4">
                                                        {kpis.map(kpi => (
                                                            <div key={kpi.label} className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-3">
                                                                <div className="flex items-start justify-between mb-2">
                                                                    <div>
                                                                        <p className="text-xs font-semibold text-slate-300">{kpi.label}</p>
                                                                        <p className="text-[10px] text-slate-500 mt-0.5 leading-tight">{kpi.sub}</p>
                                                                    </div>
                                                                    <span className={`text-sm font-bold tabular-nums ${kpi.textColor}`}>{kpi.badge}</span>
                                                                </div>
                                                                <div className="flex items-center gap-2">
                                                                    <div className="flex-1 h-1.5 rounded-full bg-slate-700 overflow-hidden">
                                                                        <div
                                                                            className={`h-full rounded-full transition-all ${kpi.barColor}`}
                                                                            style={{ width: `${kpi.pct}%` }}
                                                                        />
                                                                    </div>
                                                                    <span className="text-[10px] text-slate-500 tabular-nums shrink-0">{kpi.value}</span>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                );
                                            })()}

                                            {/* Action bar: Approve All + Re-ejecutar Pendientes */}
                                            <div className="flex items-center justify-between mb-3 gap-2">
                                                {/* Re-ejecutar Pendientes */}
                                                {(() => {
                                                    const pending = getPendingTests(result);
                                                    if (pending.length === 0) return null;
                                                    const grepPattern = pending.map(t => t.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');
                                                    const cmd = `npx playwright test --grep "${grepPattern}"`;
                                                    const isOpen = reRunPanel === result.id;
                                                    return (
                                                        <div className="flex-1">
                                                            <button
                                                                onClick={() => setReRunPanel(isOpen ? null : result.id)}
                                                                className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-yellow-500/10 border border-yellow-500/20 text-yellow-400 hover:bg-yellow-500/20 transition-colors"
                                                            >
                                                                <RotateCcw className="w-3.5 h-3.5" />
                                                                Re-ejecutar {pending.length} pendiente{pending.length !== 1 ? 's' : ''}
                                                            </button>
                                                            {isOpen && (
                                                                <div className="mt-2 bg-slate-950/70 border border-slate-700/50 rounded-lg p-3 flex items-center gap-2">
                                                                    <code className="flex-1 text-xs text-emerald-300 font-mono truncate">{cmd}</code>
                                                                    <button
                                                                        onClick={() => {
                                                                            navigator.clipboard.writeText(cmd);
                                                                            setCopied(true);
                                                                            setTimeout(() => setCopied(false), 2000);
                                                                        }}
                                                                        className="shrink-0 flex items-center gap-1 text-xs text-slate-400 hover:text-white px-2 py-1 rounded bg-slate-700/60 hover:bg-slate-600/60 transition-colors"
                                                                    >
                                                                        <Copy className="w-3.5 h-3.5" />
                                                                        {copied ? '¡Copiado!' : 'Copiar'}
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })()}

                                                {/* Approve All */}
                                                {result.passed > 0 && (
                                                    <button
                                                        onClick={() => handleApproveAll(result.id)}
                                                        disabled={approvingAll === result.id}
                                                        className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 hover:bg-emerald-500/20 transition-colors disabled:opacity-50 shrink-0"
                                                    >
                                                        <ShieldCheck className="w-3.5 h-3.5" />
                                                        {approvingAll === result.id ? 'Aprobando...' : 'Aprobar todos los PASS'}
                                                    </button>
                                                )}
                                            </div>

                                            <table className="w-full">
                                                <thead>
                                                    <tr className="text-xs text-slate-500 uppercase">
                                                        <th className="text-left pb-2 font-medium">Test</th>
                                                        <th className="text-center pb-2 font-medium w-20">Estado</th>
                                                        <th className="text-center pb-2 font-medium w-32">QA State</th>
                                                        <th className="text-center pb-2 font-medium w-36">Issue</th>
                                                        <th className="text-right pb-2 font-medium w-20">Dur.</th>
                                                        <th className="text-center pb-2 font-medium w-24">Acción</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-slate-800/50">
                                                    {result.results.map((test, i) => {
                                                        const qaState = getQAState(result, test.name);
                                                        const triageData = result.triage?.[test.name];
                                                        return (
                                                            <tr key={i} className="text-sm">
                                                                <td className="py-2 pr-4">
                                                                    <span className="text-slate-200 text-xs font-mono">{test.name}</span>
                                                                    {test.error && (
                                                                        <p className="text-xs text-red-400/80 mt-1 max-w-lg line-clamp-2">
                                                                            {test.error}
                                                                        </p>
                                                                    )}
                                                                    {triageData && (triageData.triage_note || triageData.responsable || triageData.accion_siguiente) && (
                                                                        <p className="text-xs text-slate-500 mt-1 italic">
                                                                            {triageData.triage_note}
                                                                            {triageData.defect_link && (
                                                                                <span className="ml-1 text-purple-400 not-italic">{triageData.defect_link}</span>
                                                                            )}
                                                                            {(triageData.responsable || triageData.accion_siguiente) && (
                                                                                <span className="not-italic ml-1 inline-flex gap-1">
                                                                                    {triageData.responsable && (
                                                                                        <span className="px-1.5 py-0.5 rounded bg-slate-700/60 text-slate-300 text-xs">
                                                                                            {triageData.responsable}
                                                                                        </span>
                                                                                    )}
                                                                                    {triageData.accion_siguiente && (
                                                                                        <span className="px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400 text-xs border border-blue-500/20">
                                                                                            → {triageData.accion_siguiente}
                                                                                        </span>
                                                                                    )}
                                                                                </span>
                                                                            )}
                                                                        </p>
                                                                    )}
                                                                </td>

                                                                {/* Test Status */}
                                                                <td className="py-2 text-center">
                                                                    {test.status === 'pass' && (
                                                                        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                                                                            <CheckCircle2 className="w-3 h-3" /> Pass
                                                                        </span>
                                                                    )}
                                                                    {test.status === 'fail' && (
                                                                        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-red-500/10 text-red-400 border border-red-500/20">
                                                                            <XCircle className="w-3 h-3" /> Fail
                                                                        </span>
                                                                    )}
                                                                    {test.status === 'skip' && (
                                                                        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-slate-500/10 text-slate-400 border border-slate-500/20">
                                                                            <MinusCircle className="w-3 h-3" /> Skip
                                                                        </span>
                                                                    )}
                                                                </td>

                                                                {/* QA State Badge */}
                                                                <td className="py-2 text-center">
                                                                    <div className="flex flex-col items-center gap-1">
                                                                        <QAStateBadge state={qaState} />
                                                                        {triageData?.pool_state && (
                                                                            <PoolStateBadge
                                                                                state={triageData.pool_state}
                                                                                until={triageData.quarantine_until}
                                                                            />
                                                                        )}
                                                                    </div>
                                                                </td>

                                                                {/* Issue Interno (FSQ-xxxxxx) */}
                                                                <td className="py-2 text-center">
                                                                    {triageData?.issue_id ? (
                                                                        <div className="flex flex-col items-center gap-1">
                                                                            <span className="text-xs font-mono text-orange-400 bg-orange-500/10 border border-orange-500/20 px-1.5 py-0.5 rounded">
                                                                                {triageData.issue_id}
                                                                            </span>
                                                                            <select
                                                                                value={triageData.issue_status ?? 'Abierto'}
                                                                                onChange={(e) => updateIssueStatus(result.id, test.name, e.target.value as IssueStatus)}
                                                                                className={`text-xs px-1.5 py-0.5 rounded border bg-slate-800 focus:outline-none ${
                                                                                    triageData.issue_status === 'Resuelto' || triageData.issue_status === 'Cerrado'
                                                                                        ? 'border-emerald-500/30 text-emerald-400'
                                                                                        : triageData.issue_status === 'En progreso'
                                                                                        ? 'border-blue-500/30 text-blue-400'
                                                                                        : 'border-red-500/30 text-red-400'
                                                                                }`}
                                                                            >
                                                                                {(['Abierto', 'En progreso', 'Resuelto', 'Cerrado'] as IssueStatus[]).map(s => (
                                                                                    <option key={s} value={s}>{s}</option>
                                                                                ))}
                                                                            </select>
                                                                        </div>
                                                                    ) : (
                                                                        <span className="text-xs text-slate-600">—</span>
                                                                    )}
                                                                </td>

                                                                {/* Duration */}
                                                                <td className="py-2 text-right text-xs text-slate-500">
                                                                    {test.duration_ms != null ? `${(test.duration_ms / 1000).toFixed(1)}s` : '-'}
                                                                </td>

                                                                {/* Action */}
                                                                <td className="py-2 text-center">
                                                                    <div className="flex flex-col items-center gap-1">
                                                                        {test.status !== 'skip' && (
                                                                            <button
                                                                                onClick={() => setTriageModal({ execId: result.id, test })}
                                                                                className={`text-xs px-2.5 py-1 rounded-lg border transition-colors ${
                                                                                    test.status === 'pass'
                                                                                        ? 'border-emerald-500/20 text-emerald-400 hover:bg-emerald-500/10'
                                                                                        : 'border-purple-500/20 text-purple-400 hover:bg-purple-500/10'
                                                                                }`}
                                                                            >
                                                                                {test.status === 'pass'
                                                                                    ? qaState === 'VERIFIED' ? '✓ Aprobado' : 'Aprobar'
                                                                                    : qaState === 'PENDING_REVIEW' ? 'Triaje' : 'Editar'}
                                                                            </button>
                                                                        )}
                                                                        {/* Pool: add to regression for VERIFIED, quarantine for FAIL */}
                                                                        {qaState === 'VERIFIED' && triageData?.pool_state !== 'IN_REGRESSION' && (
                                                                            <button
                                                                                onClick={() => handleAddToPool(result.id, test.name)}
                                                                                title="Agregar al pool de regresión"
                                                                                className="text-xs px-2 py-0.5 rounded border border-cyan-500/20 text-cyan-400 hover:bg-cyan-500/10 transition-colors"
                                                                            >
                                                                                <FlaskConical className="w-3 h-3 inline mr-0.5" />
                                                                                Pool
                                                                            </button>
                                                                        )}
                                                                        {test.status === 'fail' && triageData?.pool_state !== 'QUARANTINED' && (
                                                                            <button
                                                                                onClick={() => setQuarantineModal({ execId: result.id, test })}
                                                                                title="Poner en cuarentena"
                                                                                className="text-xs px-2 py-0.5 rounded border border-slate-600/50 text-slate-500 hover:text-slate-300 hover:border-slate-500 transition-colors"
                                                                            >
                                                                                <Lock className="w-3 h-3 inline mr-0.5" />
                                                                                Cuarentena
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>

                                            {/* Suite de Regresión — only shows when tests are IN_REGRESSION */}
                                            {(() => {
                                                const regTests = (result.results || []).filter(
                                                    t => result.triage?.[t.name]?.pool_state === 'IN_REGRESSION'
                                                );
                                                if (regTests.length === 0) return null;
                                                const grepCmd = `npx playwright test --grep "${regTests.map(t => t.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')}"`;
                                                return (
                                                    <div className="mt-4 border border-cyan-500/20 rounded-lg bg-cyan-500/5 p-3">
                                                        <div className="flex items-center gap-2 mb-2">
                                                            <FlaskConical className="w-3.5 h-3.5 text-cyan-400" />
                                                            <span className="text-xs font-medium text-cyan-400">
                                                                Suite de Regresión — {regTests.length} test{regTests.length !== 1 ? 's' : ''}
                                                            </span>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <code className="flex-1 text-xs text-emerald-300 font-mono truncate bg-slate-950/60 px-2 py-1 rounded">{grepCmd}</code>
                                                            <button
                                                                onClick={() => { navigator.clipboard.writeText(grepCmd); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
                                                                className="shrink-0 text-xs text-slate-400 hover:text-white px-2 py-1 rounded bg-slate-700/60 hover:bg-slate-600/60 transition-colors flex items-center gap-1"
                                                            >
                                                                <Copy className="w-3 h-3" />
                                                                {copied ? '¡Copiado!' : 'Copiar'}
                                                            </button>
                                                        </div>
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Triage Modal */}
            {triageModal && (
                <TriageModal
                    caseName={triageModal.test.name}
                    testStatus={triageModal.test.status}
                    current={results.find(r => r.id === triageModal.execId)?.triage?.[triageModal.test.name]}
                    onClose={() => setTriageModal(null)}
                    onSubmit={async (qa_state, triage_type, note, link, responsable, accion_siguiente) => {
                        await handleTriage(
                            triageModal.execId,
                            triageModal.test.name,
                            qa_state,
                            triage_type,
                            note,
                            link,
                            responsable,
                            accion_siguiente
                        );
                    }}
                />
            )}

            {/* Quarantine Modal */}
            {quarantineModal && (
                <QuarantineModal
                    caseName={quarantineModal.test.name}
                    onClose={() => setQuarantineModal(null)}
                    onSubmit={async (reason, until_date) => {
                        await handleQuarantine(quarantineModal.execId, quarantineModal.test.name, reason, until_date);
                    }}
                />
            )}
        </div>
    );
};

export default McpResultsPanel;
